import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  chatbotV2Service,
  type ChannelAccountStatus,
  type ChannelsOverview,
  type ChatChannel,
} from "../../../services/chatbotV2/chatbotV2Service";
import { clearCachedRequest } from "../../../utils/requestCache";
import {
  isChannelConnected,
  normalizeChannelsMap,
} from "../../../utils/normalizeChannelsOverview";
import { clearIntegrationsSnapshot } from "./useIntegrations";

const CHAT_CHANNELS: ChatChannel[] = ["whatsapp", "messenger", "instagram"];

export const invalidateChannelCaches = () => {
  clearIntegrationsSnapshot();
  for (const key of ["integrations:"]) {
    clearCachedRequest(key);
  }
};

export const useChatChannelConnections = (enabled = true) => {
  const [overview, setOverview] = useState<ChannelsOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return null;
    setLoading(true);
    try {
      const data = await chatbotV2Service.getChannels();
      setOverview(data);
      return data;
    } catch {
      setOverview(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const statuses = normalizeChannelsMap(overview?.channels);

  const runChannelAction = useCallback(
    async (channel: ChatChannel, actionKey: string, action: () => Promise<void>) => {
      setActionLoading(`${channel}:${actionKey}`);
      try {
        await action();
        invalidateChannelCaches();
        await refresh();
      } finally {
        setActionLoading(null);
      }
    },
    [refresh]
  );

  const disconnect = useCallback(
    async (channel: ChatChannel) => {
      await runChannelAction(channel, "disconnect", async () => {
        await chatbotV2Service.disconnectChannel(channel);
        toast.success("Canal desconectado");
      });
    },
    [runChannelAction]
  );

  const diagnose = useCallback(
    async (channel: ChatChannel) => {
      let result: Record<string, unknown> | null = null;
      await runChannelAction(channel, "diagnose", async () => {
        result = await chatbotV2Service.diagnoseChannel(channel);
      });
      return result;
    },
    [runChannelAction]
  );

  const testSend = useCallback(
    async (channel: ChatChannel, recipient: string, text: string) => {
      let result: Record<string, unknown> | null = null;
      await runChannelAction(channel, "test", async () => {
        result = await chatbotV2Service.testChannelSend(channel, { text, recipient_id: recipient });
        toast.success("Mensaje de prueba enviado");
      });
      return result;
    },
    [runChannelAction]
  );

  const getStatus = useCallback(
    (channel: ChatChannel): ChannelAccountStatus | null => statuses[channel] ?? null,
    [statuses]
  );

  const connectedMap = CHAT_CHANNELS.reduce<Record<string, boolean>>((acc, channel) => {
    acc[channel] = isChannelConnected(statuses[channel]);
    return acc;
  }, {});

  return {
    overview,
    statuses,
    connectedMap,
    loading,
    actionLoading,
    refresh,
    getStatus,
    disconnect,
    diagnose,
    testSend,
    invalidateChannelCaches,
  };
};
